"use client";

import { useState, useEffect } from "react";
import Pagination from "../../Common/Pagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, ListOrdered, Loader2 } from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";
import { toast } from "react-hot-toast";

const TYPES = [{ value: "allowance", label: "Allowance" }, { value: "deduction", label: "Deduction" }];
const CALC_TYPES = [{ value: "fixed", label: "Fixed" }, { value: "percentage", label: "Percentage" }, { value: "formula", label: "Formula" }];
const defaultForm = { name: "", code: "", type: "allowance", calculationType: "fixed", value: "", formula: "", description: "" };

export default function AllowanceDeductionList() {
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/allowance-deduction-heads");
      setHeads(res.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load");
      setHeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(defaultForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      name: row.name,
      code: row.code,
      type: row.type,
      calculationType: row.calculationType || "fixed",
      value: String(row.value ?? ""),
      formula: row.formula || "",
      description: row.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name?.trim() || !form.code?.trim()) { toast.error("Name and code required"); return; }
    setSubmitLoading(true);
    try {
      const payload = { ...form, value: Number(form.value) || 0, formula: form.formula?.trim() || "" };
      if (editingId) await axiosInstance.put(`/allowance-deduction-heads/${editingId}`, payload);
      else await axiosInstance.post("/allowance-deduction-heads", payload);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSubmitLoading(true);
    try {
      await axiosInstance.delete(`/allowance-deduction-heads/${toDelete._id}`);
      toast.success("Deleted");
      setDeleteDialogOpen(false);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
              <ListOrdered className="h-5 w-5 text-[#0263cc]" />
              Allowance / Deduction List
            </CardTitle>
            <Button onClick={openCreate} className="bg-[#0263cc] hover:bg-[#0255b0] text-white shrink-0">
              <Plus className="h-4 w-4 mr-2" /> Add Head
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-1">Define salary heads for allowances and deductions (fixed, percentage or formula).</p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">Code</TableHead>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Calculation</TableHead>
                  <TableHead className="font-semibold">Value</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {heads.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No heads yet. Add one to get started.</TableCell></TableRow>
                ) : (
                  heads.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((row) => (
                    <TableRow key={row._id} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <Badge className={row.type === "allowance" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{row.type}</Badge>
                      </TableCell>
                      <TableCell>{row.calculationType || "fixed"}</TableCell>
                      <TableCell>{row.calculationType === "percentage" ? `${row.value}%` : row.value}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(row)} className="text-slate-600"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => { setToDelete(row); setDeleteDialogOpen(true); }} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
          <Pagination
            currentPage={page}
            totalItems={heads.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setPage}
            onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
            itemLabel="heads"
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0263cc]">{editingId ? "Edit Head" : "Add Head"}</DialogTitle>
            <DialogDescription>Allowance or deduction head with calculation type.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name <span className="text-red-500">*</span></Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. HRA" /></div>
              <div className="space-y-2"><Label>Code <span className="text-red-500">*</span></Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. HRA" disabled={!!editingId} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Type <span className="text-red-500">*</span></Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Calculation <span className="text-red-500">*</span></Label>
                <Select value={form.calculationType} onValueChange={(v) => setForm((f) => ({ ...f, calculationType: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CALC_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Value</Label><Input type="number" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} placeholder="0 or %" /></div>
            {form.calculationType === "formula" && (
              <div className="space-y-2"><Label>Formula (optional)</Label><Input value={form.formula} onChange={(e) => setForm((f) => ({ ...f, formula: e.target.value }))} placeholder="e.g. basic * 0.1" /></div>
            )}
            <div className="space-y-2"><Label>Description (optional)</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitLoading} className="bg-[#0263cc] hover:bg-[#0255b0] text-white">{submitLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingId ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Head</DialogTitle><DialogDescription>Remove &quot;{toDelete?.name}&quot;? This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setToDelete(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitLoading}>{submitLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
